/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import alertsConfigCreateNewConfiguration from '../configuration/alerts-config-create-new.configuration';
import {AlertsCreateNewInputEnum} from '../models/alerts-create-new-input.enum';
import {WarningsEnum} from '../../../../shared/warnings/warnings.enum';
import {InputTextComponent} from '../../../../shared/input/input-text/input-text.component';
import {InputTextareaComponent} from '../../../../shared/input/input-textarea/input-textarea.component';
import {InputDatepickerComponent} from '../../../../shared/input/input-datepicker/input-datepicker.component';
import {
    AbstractControl,
    ValidatorFn
} from '@angular/forms';
import {objectsValidator} from '../../../../shared/input/input-validators';
import {MandatoryMessageTypeEnum} from '../../../../shared/input/service/mandatory-message-type.enum';
import {
    inputDataValidatorDate,
    inputDataValidatorText
} from '../../../../shared/utils/input-data-validator/input-data-validator';
import {
    NgbCalendar,
    NgbDate
} from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment-timezone';
import {UnsavedChangesService} from '../../../../shared/guards/unsaved-work/unsaved-changes.service';
import {DatepickerHelperService} from '../../../../shared/utils/datepicker-helper/datepicker-helper.service';
import {InputMessagesService} from '../../../../shared/input/service/input-messages.service';
import {StatefulComponent} from '../../../../shared/stateful/stateful.component';
import {AlertEditableFormState} from './models/alert-editable-form.state';
import {EditAlertConfigModel} from '../../alerts-main-page/models/edit-alert-config.model';
import {Subscription} from 'rxjs';
import {ReplayableRouterEventsService} from '../../../../shared/services/replayable-router-events.service';
import {AlertEditableFormDataModel} from './models/alert-editable-form-data.model';
import {EditAlertConfigDataModel} from '../../alerts-main-page/models/edit-alert-config-data.model';

@Component({
    selector: 'app-alert-editable-form',
    templateUrl: './alert-editable-form.component.html',
    styleUrls: ['./alert-editable-form.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AlertEditableFormComponent extends StatefulComponent<AlertEditableFormState>
        implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @Input() editAlertConfig: EditAlertConfigModel;
    @Input() isViewMode: boolean;
    @Output() onFormDataChanges = new EventEmitter<AlertEditableFormDataModel>();

    @ViewChild('alertNameInput') alertNameInput: InputTextComponent;
    @ViewChild('alertContentInput', {static: true}) alertContentInput: InputTextareaComponent;
    @ViewChild('scheduleStartDateInput', {static: false}) scheduleStartDateInput: InputDatepickerComponent;
    @ViewChild('scheduleEndDateInput', {static: false}) scheduleEndDateInput: InputDatepickerComponent;

    alertsConfigCreateNewConfiguration = alertsConfigCreateNewConfiguration;
    alertNameInputMessages = new Map<WarningsEnum, string[]>();
    alertContentInputMessages = new Map<WarningsEnum, string[]>();
    scheduleStartDateInputMessages = new Map<WarningsEnum, string[]>();
    scheduleEndDateInputMessages = new Map<WarningsEnum, string[]>();
    WarningEnum = WarningsEnum;
    AlertsCreateNewInputEnum = AlertsCreateNewInputEnum;
    validators: { [key: string]: ValidatorFn[] } = {
        scheduleStartDate: [objectsValidator],
        scheduleEndDate: [objectsValidator]
    };
    private subscriptions = new Subscription();

    constructor(private unsavedChangesService: UnsavedChangesService,
                private cdr: ChangeDetectorRef,
                private calendar: NgbCalendar,
                public datepickerHelperService: DatepickerHelperService,
                private inputMessagesService: InputMessagesService,
                private replay: ReplayableRouterEventsService) {
        super();
    }

    get startDateWarningLevel(): WarningsEnum | null {
        return this.state.alertScheduleActivationSwitchButtonModel.checked ? this.WarningEnum.WARNING_APPLY : null;
    }

    ngOnInit(): void {
        // Initial data that is presented on a NEW alert
        this.storeState({
            isInitialFormDataLoaded: false,
            isValidToApply: false,
            alertActivationSwitchButtonModel: {
                checked: false,
                disabled: this.isViewMode
            },
            alertScheduleActivationSwitchButtonModel: {
                checked: false,
                disabled: this.isViewMode
            },
            alertConfigData: {
                ...this.state.alertConfigData,
                active: false
            },
            scheduleDateInputsDisabled: true,
            minScheduleStartDate: this.datepickerHelperService.todayDate(alertsConfigCreateNewConfiguration.timeZone),
            minScheduleEndDate: this.datepickerHelperService.todayDate(alertsConfigCreateNewConfiguration.timeZone)
        });
    }

    ngAfterViewInit(): void {
        // If it's an alert in edit mode it proceeds to load the form data
        if (this.editAlertConfig != null) {
            this.loadFormDataToState();
        }
        this.storeState({
            isInitialFormDataLoaded: true
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['isViewMode'].firstChange) {
            this.loadFormDataToState();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    manageAlertNameInputStateChange(alertName: AbstractControl): void {
        this.inputMessagesService.mandatoryWarnings(alertName,
                this.alertNameInputMessages,
                MandatoryMessageTypeEnum.APPLY);
        this.alertNameInputMessages = new Map(this.alertNameInputMessages);
    }

    manageAlertContentInputStateChange(alertContent: AbstractControl): void {
        this.inputMessagesService.mandatoryWarnings(alertContent,
                this.alertContentInputMessages,
                MandatoryMessageTypeEnum.APPLY);
        this.alertContentInputMessages = new Map(this.alertContentInputMessages);
    }

    manageScheduleStartDateInputStateChange(startDate: AbstractControl): void {
        this.inputMessagesService.invalidInputValueError(startDate,
                this.scheduleStartDateInputMessages, 'date');
        this.inputMessagesService.mandatoryWarnings(startDate,
                this.scheduleStartDateInputMessages,
                MandatoryMessageTypeEnum.APPLY);
        this.scheduleStartDateInputMessages = new Map(this.scheduleStartDateInputMessages);
    }

    manageScheduleEndDateInputStateChange(endDate: AbstractControl): void {
        this.inputMessagesService.invalidInputValueError(endDate,
                this.scheduleEndDateInputMessages, 'date');
        this.scheduleEndDateInputMessages = new Map(this.scheduleEndDateInputMessages);
    }

    formInputValueHandlerText(value: string, inputType: AlertsCreateNewInputEnum): void {
        switch (inputType) {
            case AlertsCreateNewInputEnum.NAME:
                this.storeState({
                    alertConfigData: {
                        ...this.state.alertConfigData,
                        name: inputDataValidatorText(this.alertNameInput, value)
                    }
                });
                break;
            case AlertsCreateNewInputEnum.DESCRIPTION:
                this.storeState({
                    alertConfigData: {
                        ...this.state.alertConfigData,
                        description: inputDataValidatorText(this.alertContentInput, value)
                    }
                });
                break;
        }
        this.alertsConfigFormDataInputsIsValid();
    }

    formInputValueHandlerDate(value: NgbDate, inputType: AlertsCreateNewInputEnum): void {
        const todayDateTimestamp = moment()
                .tz(alertsConfigCreateNewConfiguration.timeZone)
                .valueOf();
        let convertedNgbDateStartValue = this.datepickerHelperService
                .convertNgbDateToTimestampUtcStartDate(value);

        if (isNaN(convertedNgbDateStartValue) || convertedNgbDateStartValue == null) {
            convertedNgbDateStartValue = todayDateTimestamp;
        }

        switch (inputType) {
            case AlertsCreateNewInputEnum.SCHEDULE_STARTS_ON:
                this.storeState({
                    alertConfigData: {
                        ...this.state.alertConfigData,
                        scheduleStartsOn: inputDataValidatorDate(
                                this.scheduleStartDateInput,
                                convertedNgbDateStartValue,
                                this.hasInputErrorMessages(this.scheduleStartDateInputMessages, true),
                                true)
                    }
                });
                this.state.minScheduleStartDate = this.datepickerHelperService.convertTimestampTimeZoneToNgbDate(
                        alertsConfigCreateNewConfiguration.timeZone,
                        Math.min(todayDateTimestamp, this.editAlertConfig?.editAlertConfigData?.startsOn ?? Number.MAX_SAFE_INTEGER));
                this.state.minScheduleEndDate = this.datepickerHelperService.convertTimestampTimeZoneToNgbDate(
                        alertsConfigCreateNewConfiguration.timeZone,
                        Math.max(convertedNgbDateStartValue, todayDateTimestamp));
                break;
            case AlertsCreateNewInputEnum.SCHEDULE_ENDS_ON:
                this.storeState({
                    alertConfigData: {
                        ...this.state.alertConfigData,
                        scheduleEndsOn: inputDataValidatorDate(
                                this.scheduleEndDateInput,
                                this.datepickerHelperService
                                        .convertNgbDateToTimestampUtcEndDate(value),
                                this.hasInputErrorMessages(this.scheduleStartDateInputMessages, false),
                                false)
                    }
                });
                break;
        }
        this.cdr.detectChanges();
        this.alertsConfigFormDataInputsIsValid();
    }

    formInputValueHandlerActive(value: boolean, inputType: AlertsCreateNewInputEnum): void {
        if (inputType === AlertsCreateNewInputEnum.ACTIVE) {
            this.storeState({
                alertActivationSwitchButtonModel: {
                    checked: value,
                    disabled: this.isViewMode
                },
                alertConfigData: {
                    ...this.state.alertConfigData,
                    active: value
                }
            });
        }
        this.alertsConfigFormDataInputsIsValid();
    }

    switchAlertScheduleActivation(checked: boolean): void {
        this.storeState({
            scheduleDateInputsDisabled: !checked
        });
        this.scheduleEndDateInput.inputControl.enable();
        this.scheduleStartDateInput.inputControl.enable();

        if (checked) {
            this.scheduleStartDateInput.setValue(this.state.minScheduleStartDate);
            this.formInputValueHandlerDate(this.state.minScheduleStartDate, AlertsCreateNewInputEnum.SCHEDULE_STARTS_ON);
            this.storeState({
                minScheduleEndDate: this.datepickerHelperService.todayDate(alertsConfigCreateNewConfiguration.timeZone)
            });
        } else {
            this.scheduleStartDateInput.setValue(null);
            this.scheduleEndDateInput.setValue(null);
            delete this.state.alertConfigData.scheduleStartsOn;
            delete this.state.alertConfigData.scheduleEndsOn;

            this.scheduleEndDateInput.inputControl.disable();
            this.scheduleStartDateInput.inputControl.disable();
        }
        this.alertsConfigFormDataInputsIsValid();
    }

    onDatePickerValueChange(value: string, inputType: AlertsCreateNewInputEnum): void {
        let mandatoryDateIsValid = true;
        if (inputType === AlertsCreateNewInputEnum.SCHEDULE_STARTS_ON && value == null) {
            mandatoryDateIsValid = false;
        }
        this.storeState({
            isValidToApply: mandatoryDateIsValid && this.isFormEligibleToApply()
        });
        this.propagateFormChanges();
    }

    showInputDatetimepickers(): boolean {
        return !this.isViewMode || this.editAlertConfig.editAlertConfigData.scheduled;
    }

    private alertsConfigFormDataInputsIsValid(): void {
        this.subscriptions.add(
                this.replay.onGuardsCheckStart.subscribe(() => {
                    this.unsavedChangesService.currentValue = this.state.alertConfigData;
                })
        );
        this.storeState({
            isValidToApply: this.unsavedChangesService.isChanged() && this.isFormEligibleToApply()
        });
        this.propagateFormChanges();
    }

    private loadFormDataToState(): void {
        this.storeState({
            alertScheduleActivationSwitchButtonModel: {
                checked: this.editAlertConfig.editAlertConfigData.scheduled,
                disabled: this.isViewMode
            },
            isInitialFormDataLoaded: false
        });

        this.formInputValueHandlerActive(this.editAlertConfig.editAlertConfigData.active, AlertsCreateNewInputEnum.ACTIVE);
        this.formInputValueHandlerText(this.editAlertConfig.editAlertConfigData.name, AlertsCreateNewInputEnum.NAME);
        this.formInputValueHandlerText(this.editAlertConfig.editAlertConfigData.description, AlertsCreateNewInputEnum.DESCRIPTION);

        this.formInputValueHandlerDate(this.state.startDateNgbDate, AlertsCreateNewInputEnum.SCHEDULE_STARTS_ON);
        this.formInputValueHandlerDate(this.state.endDateNgbDate, AlertsCreateNewInputEnum.SCHEDULE_ENDS_ON);
        this.validateSchedulerDates();

        this.storeState({isInitialFormDataLoaded: true});

        this.unsavedChangesService.originalValue = this.state.alertConfigData;
        this.cdr.detectChanges();
    }

    private validateSchedulerDates(): void {
        let startDateNgbDate: NgbDate;
        let endDateNgbDate: NgbDate;

        if (this.editAlertConfig.editAlertConfigData.scheduled === true) {
            startDateNgbDate = this.datepickerHelperService
                    .convertUtcTimestampToNgbDate(this.editAlertConfig.editAlertConfigData.startsOn);
            this.scheduleStartDateInput.setValue(startDateNgbDate);
            this.scheduleStartDateInput.closedDatepickerHandler();

            if (this.editAlertConfig.editAlertConfigData.endsOn != null) {
                endDateNgbDate = this.datepickerHelperService
                        .convertUtcTimestampToNgbDate(this.editAlertConfig.editAlertConfigData.endsOn);
                this.scheduleEndDateInput.setValue(endDateNgbDate);
                this.scheduleEndDateInput.closedDatepickerHandler();
            }
            this.scheduleEndDateInput.inputControl.enable();
            this.scheduleStartDateInput.inputControl.enable();
        }

        this.storeState({
            startDateNgbDate: startDateNgbDate,
            endDateNgbDate: endDateNgbDate
        });
    }

    private isFormEligibleToApply(): boolean {
        return this.state.alertConfigData?.name?.isValid &&
                this.state.alertConfigData?.description?.isValid &&
                this.alertsConfigFormDataInputsDateIsValid();
    }

    private alertsConfigFormDataInputsDateIsValid(): boolean {
        let isValid = true;
        if (this.state.alertScheduleActivationSwitchButtonModel.checked) {
            isValid = !this.hasInputErrorMessages(this.scheduleStartDateInputMessages, true)
                    && this.scheduleStartDateInput.isValid && this.scheduleEndDateInput.isValid;
        }
        return isValid;
    }

    private propagateFormChanges(): void {
        if (this.state.isInitialFormDataLoaded) {
            const event: AlertEditableFormDataModel = {
                alertConfigData: this.state.alertConfigData,
                isAlertScheduleActive: this.state.alertScheduleActivationSwitchButtonModel.checked,
                isFormEligibleToApply: !!this.state.isValidToApply
            };
            this.onFormDataChanges.emit(event);
        }
    }

    private hasInputErrorMessages(inputMessages: Map<WarningsEnum, string[]>, verifyWarnings: boolean): boolean {
        let warnings = 0;
        const errors = inputMessages?.get(WarningsEnum.ERROR)?.length ?? 0;
        if (verifyWarnings) {
            warnings = inputMessages?.get(WarningsEnum.WARNING)?.length ?? 0;
        }
        return (errors + warnings) > 0;
    }
}
